import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../env";
import Loading from "../../../components/loading";
import ErrorFrame from "../../../components/error";
import HomeEnrollmentsChart from "./HomeEnrollmentsChart";

const round2 = (v) => {
    const n = parseFloat(v);
    if (isNaN(n)) return "-";
    if (n === 0) return "0";
    return n.toFixed(2);
};

function KpiCard({ value, unit, label }) {
    return (
        <div className="card standard-padding-s flex columns gap-xs">
            <div className="flex gap-xxs center">
                <p className="section-title-m medium">{value}</p>
                {unit && <p className="card-body neutral">{unit}</p>}
            </div>
            <p className="card-label">{label}</p>
        </div>
    );
}

function TrackBar({ track, total, index }) {
    const pct = total > 0 ? (track.totalMatches / total) * 100 : 0;
    const fillClass = index % 2 === 0 ? "track-bar-fill" : "track-bar-fill accent-fill";
    return (
        <div className="v-spacer-s">
            <div className="flex space-between center v-spacer-xxs">
                <p className="card-body">{track.trackName}</p>
                <p className="card-body medium">{track.totalMatches}</p>
            </div>
            <div className="flex center gap-s">
                <div className="track-bar-bg">
                    <div className={fillClass} style={{ width: `${pct}%` }}></div>
                </div>
            </div>
        </div>
    );
}

export default function HomeDashboard({ programId }) {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);

    const fetchStats = () => {
        setStats(null);
        setError(null);
        if (!programId) return;
        api.get(`programs/${programId}/stats`)
            .then(res => setStats(res.data))
            .catch(err => setError(err.message));
    };

    useEffect(() => fetchStats(), [programId]);

    if (error) return <ErrorFrame onAction={fetchStats} />;

    if (!stats) return (
        <div className="card standard-padding-s flex justify-center center" style={{ minHeight: 120 }}>
            <Loading message="Loading dashboard..." />
        </div>
    );

    const kpis = [
        { value: stats.totalMatches, label: "Active pairings" },
        { value: `${round2(stats.matchSuccessRate * 100)}%`, label: "Match success rate" },
        { value: `${round2(stats.rateOfCompletedSessions * 100)}%`, label: "Sessions completed" },
        { value: `${round2(stats.engagementRate)}%`, label: "Engagement rate" },
    ];

    const trackStats = stats.trackStats ?? [];
    const totalMatches = stats.totalMatches || 1;

    return (
        <div>
            <div className="dashboard-kpi-row v-spacer-xs">
                {kpis.map((kpi, i) => (
                    <KpiCard key={i} value={kpi.value} unit={kpi.unit} label={kpi.label} />
                ))}
            </div>

            <div className="two-columns gap-xs v-spacer-xs">
                <div className="card standard-padding-s h-100">
                    <div className="flex space-between center v-spacer-l">
                        <p className="card-title">Active pairings by track</p>
                        <p className="card-label neutral">{stats.totalMatches} total</p>
                    </div>
                    {trackStats.length === 0 && (
                        <p className="card-label neutral">No track data available.</p>
                    )}
                    {trackStats.map((track, i) => (
                        <TrackBar key={track.trackId} track={track} total={totalMatches} index={i} />
                    ))}
                </div>

                <div className="card standard-padding-s h-100">
                    <HomeEnrollmentsChart programId={programId} />
                </div>
            </div>

            <div className="flex justify-end">
                <button className="outline" onClick={() => navigate('stats')}>View more statistics</button>
            </div>
        </div>
    );
}
